'use client'

import Bugsnag from '@bugsnag/js'
import { TailwindConfig } from '@sedomicilier/material-sd'
import { Spin } from 'antd'
import { createContext, useEffect, useState } from 'react'
import TC_Wrapper from 'react-tag-commander'
import md5 from 'md5'
import { Contact } from '@/types/contact'
import { useQuery } from '@apollo/client'
import { GET_TAG_COMMANDER_VARS } from '@/graphql/queries/tcVars'
import { usePathname } from '@/navigation'

interface TagCommanderProps {
  children: React.ReactNode
}

interface TagCommanderContextProps {
  trackConversion?: (contact: Contact, tcVars: string) => void
  notifyTransactionEvent?: (contact: Contact) => void
  trackPage?: (path: string) => void
  tcVars?: string
}

export const TagCommanderContext = createContext<TagCommanderContextProps>({})

const TagCommander: React.FC<TagCommanderProps> = ({ children }) => {
  const pathname = usePathname()

  const wrapper = TC_Wrapper.getInstance()
  const prodEnv = process.env.NEXT_PUBLIC_REACT_APP_ENVIRONMENT === 'production'
  const tcProd = prodEnv ? '' : '/uat'
  const containersIds = ['20', '22', '24']

  const [tcReady, setTcReady] = useState(false)

  const { data } = useQuery(GET_TAG_COMMANDER_VARS, {
    fetchPolicy: 'no-cache'
  })

  const domiciliationId = data && JSON.parse(data.tcVars).domiciliation_id

  const device = () => {
    const desktop = parseInt(TailwindConfig.breakpoints.desktop.min, 10)
    const tablet = parseInt(TailwindConfig.breakpoints.tablet.min, 10)

    if (window.innerWidth > desktop)
      return 'd' // desktop
    else if (window.innerWidth > tablet)
      return 't' // tablet
    else return 'm' // mobile
  }

  const commonVars = {
    env_category: 'subflow',
    env_work: prodEnv ? 'prod' : 'preprod',
    env_page: window.location.pathname,
    env_url: window.location.href,
    env_device: device()
  }

  const reloadTcVars = () => {
    const newTcVars = JSON.parse(data?.tcVars)
    const varsToPush = {
      ...newTcVars,
      ...commonVars
    }
    wrapper.setTcVars(varsToPush)
    wrapper.reloadAllContainers()
  }

  const notifyLeadEvent = (contact: Contact, tcVars: string) => {
    const baseVars = {
      ...JSON.parse(tcVars),
      ...commonVars
    }
    const vars = {
      domiciliation_id: JSON.parse(tcVars).domiciliation_id,
      domiciliation_phone_number: contact.phoneNumber,
      domiciliation_phone_number_md5: md5(contact.phoneNumber || ''),
      domiciliation_email: contact.email,
      domiciliation_email_md5: md5(contact.email || ''),
      ...baseVars
    }

    wrapper.setTcVars(vars)

    try {
      wrapper.triggerEvent('lead', null, {
        event_category: '',
        event_label: 'Domiciliation',
        event_action: 'lead',
        ...vars
      })
    } catch (error) {
      Bugsnag.notify('TagCommander Event failed : ' + error)
      return undefined
    }

    wrapper.reloadAllContainers()
  }

  const trackConversion = (contact: Contact, tcVars: string) => {
    notifyLeadEvent(contact, tcVars)

    if (prodEnv) {
      // notify HUBSPOT
      const _hsq = (window._hsq = window._hsq || [])
      if (_hsq) {
        _hsq.push([
          'identify',
          {
            email: contact.email
          }
        ])
        _hsq.push(['setPath', '/'])
        _hsq.push(['trackPageView'])
      }
    }
  }

  const notifyTransactionEvent = (contact: Contact) => {
    try {
      reloadTcVars()
      const baseVars = {
        ...JSON.parse(data?.tcVars),
        ...commonVars
      }
      const vars = {
        event_category: '',
        event_label: 'Transaction',
        event_action: 'transaction',
        domiciliation_id: domiciliationId,
        domiciliation_phone_number: contact.phoneNumber,
        domiciliation_phone_number_md5: md5(contact.phoneNumber || ''),
        domiciliation_email: contact.email,
        domiciliation_email_md5: md5(contact.email || ''),
        ...baseVars
      }

      wrapper.triggerEvent('transaction', null, vars)
    } catch (error) {
      Bugsnag.notify('TagCommander Event failed : ' + error)
      return undefined
    }
  }

  const trackPage = () => {
    wrapper.trackPageLoad({ tcVars: { page: pathname.replace('/', '') } })
  }

  useEffect(() => {
    const tagId =
      window.location.host === process.env.NEXT_PUBLIC_APP_HOST_FR
        ? '5477'
        : '5881'

    Promise.all([
      containersIds.map((containerId: string) =>
        wrapper.addContainer(
          containerId,
          `https://cdn.tagcommander.com/${tagId}${tcProd}/tc_SeDomicilier_${containerId}.js`,
          'head'
        )
      )
    ]).then(() => {
      setTcReady(true)
    })
  }, [])

  useEffect(() => {
    if (tcReady) reloadTcVars()
  }, [data])

  useEffect(() => {
    trackPage()
  }, [pathname])

  if (!tcReady)
    return (
      <>
        <div className='absolute top-[50%] left-[50%]'>
          <Spin size='large' />
        </div>
      </>
    )

  return (
    <TagCommanderContext.Provider
      value={{
        trackConversion,
        notifyTransactionEvent,
        trackPage,
        tcVars: data?.tcVars
      }}
    >
      {children}
    </TagCommanderContext.Provider>
  )
}

export default TagCommander
